import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token refresh on 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        isRefreshing = false;
        authAPI.clearAuth();
        window.location.href = '/admin/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_BASE}/admin/refresh`, {
          refresh_token: refreshToken,
        });

        const { access_token } = response.data;
        localStorage.setItem('adminToken', access_token);
        
        api.defaults.headers.Authorization = `Bearer ${access_token}`;
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        
        processQueue(null, access_token);
        
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        authAPI.clearAuth();
        window.location.href = '/admin/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// About Content API
export const aboutAPI = {
  get: () => api.get('/about'),
  update: (data) => api.put('/about', data),
  addSection: (section) => api.post('/about/sections', section),
  updateSection: (id, section) => api.put(`/about/sections/${id}`, section),
  deleteSection: (id) => api.delete(`/about/sections/${id}`),
};

// Portfolio API
export const portfolioAPI = {
  getAll: (category) => api.get('/portfolio', { params: { category } }),
  create: (project) => api.post('/portfolio', project),
  update: (id, project) => api.put(`/portfolio/${id}`, project),
  delete: (id) => api.delete(`/portfolio/${id}`),
};

// Contact/Messages API
export const contactAPI = {
  submit: (message) => api.post('/messages/contact', message),
  getAll: () => api.get('/messages'),
  update: (id, update) => api.put(`/messages/${id}`, update),
  delete: (id) => api.delete(`/messages/${id}`),
};

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/admin/login', credentials),
  verify: () => api.get('/admin/verify'),
  refresh: (refreshToken) => api.post('/admin/refresh', { refresh_token: refreshToken }),
  logout: (refreshToken) => api.post('/admin/logout', { refresh_token: refreshToken }),
  logoutAll: () => api.post('/admin/logout-all'),
  changePassword: (passwordData) => api.post('/admin/change-password', passwordData),
  clearAuth: () => {
    localStorage.removeItem('adminAuth');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('adminUsername');
  },
};

export default api;
